import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { SectionLabel, TiltCard } from '../components/Effects';
import { ArrowIcon, CheckIcon, DiscordIcon, ShieldIcon, SparkIcon } from '../components/Icons';
import type { CitizenProfileData, SessionUser } from '../types';

interface ProfilePageProps {
  user: SessionUser | null;
  loadingUser: boolean;
  onUserChange: (user: SessionUser | null) => void;
}

type TxFilter = 'all' | 'income' | 'outflow' | 'market' | 'shop' | 'transfer' | 'tax' | 'patent';

function formatLkr(value: number): string {
  return value.toLocaleString('en-US', {
    minimumFractionDigits: value % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  });
}

function formatTimestamp(ms: number | null): string {
  if (!ms) return 'No record';
  try {
    return new Date(ms).toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return 'No record';
  }
}

function walletArtForTier(tier: number): { img: string; className: string; badge: string } {
  if (tier >= 3) {
    return { img: '/assets/note_100000.webp', className: 'diamond', badge: 'DIAMOND BRIEFCASE • TIER III' };
  }
  if (tier === 2) {
    return { img: '/assets/note_10000.webp', className: 'gold', badge: 'GOLD PURSE • TIER II' };
  }
  return { img: '/assets/note_100.webp', className: 'leather', badge: 'LEATHER WALLET • TIER I' };
}

export function ProfilePage({ user, loadingUser, onUserChange }: ProfilePageProps) {
  const navigate = useNavigate();
  const [profile, setProfile] = useState<CitizenProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [txFilter, setTxFilter] = useState<TxFilter>('all');
  const [txSearch, setTxSearch] = useState('');
  const [loggingOut, setLoggingOut] = useState(false);

  const loadProfile = () => {
    setLoading(true);
    setError(null);
    fetch('/api/profile')
      .then(async (res) => {
        if (res.status === 401) {
          onUserChange(null);
          throw new Error('Please sign in with Discord to view your Citizen Dossier.');
        }
        if (!res.ok) {
          throw new Error('Could not load your Citizen Dossier right now.');
        }
        return res.json() as Promise<CitizenProfileData>;
      })
      .then((data) => {
        setProfile(data);
        if (data.user) onUserChange(data.user);
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Unable to load profile.');
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (loadingUser) return;
    if (!user) {
      setLoading(false);
      return;
    }
    loadProfile();
  }, [loadingUser, user?.id]);

  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      await fetch('/api/logout', { method: 'POST' });
    } catch {
      // fallback redirect
    } finally {
      onUserChange(null);
      navigate('/');
    }
  };

  const filteredTransactions = useMemo(() => {
    const items = profile?.transactions.items || [];
    const q = txSearch.trim().toLowerCase();
    return items.filter((tx) => {
      if (txFilter === 'income' && !tx.isIncome) return false;
      if (txFilter === 'outflow' && tx.isIncome) return false;
      if (txFilter === 'market' && tx.category !== 'market') return false;
      if (txFilter === 'shop' && tx.category !== 'shop') return false;
      if (txFilter === 'transfer' && tx.category !== 'transfer') return false;
      if (txFilter === 'tax' && tx.category !== 'tax') return false;
      if (txFilter === 'patent' && tx.category !== 'patent' && tx.category !== 'loan') return false;

      if (q) {
        const hay = `${tx.id} ${tx.date} ${tx.type} ${tx.target} ${tx.amount} ${tx.categoryLabel}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [profile, txFilter, txSearch]);

  if (!loadingUser && !user) {
    return (
      <section className="page-hero profile-hero">
        <div className="page-hero-bg" />
        <div className="profile-unauth-card">
          <div className="eyebrow">
            <span /> SOVEREIGN CITIZEN PORTAL
          </div>
          <h1>
            Identify yourself to access the <em>Central Ledger.</em>
          </h1>
          <p>
            Sign in with your official Discord account to view your whitelist status, State Bank balance, physical
            wallets, tax assessments, industrial patents, and full transaction history.
          </p>
          <div className="hero-cta-row">
            <a className="primary-btn" href="/auth/discord">
              <DiscordIcon />
              <span>Continue with Discord</span>
            </a>
            <Link className="ghost-btn" to="/">
              <span>Return Home</span>
            </Link>
          </div>
        </div>
      </section>
    );
  }

  const activeUser = profile?.user || user;
  const status = activeUser?.status || null;
  const statusLabel =
    status === 'passed'
      ? '✓ WHITELISTED CITIZEN'
      : status === 'pending'
      ? '⌛ APPLICATION UNDER REVIEW'
      : status === 'rejected'
      ? '✕ APPLICATION REJECTED'
      : activeUser?.inGuild
      ? 'DISCORD MEMBER • NOT APPLIED'
      : 'DISCORD JOIN REQUIRED';

  return (
    <>
      <section className="page-hero profile-hero">
        <div className="page-hero-bg" data-parallax="12" />
        <div className="profile-header-shell">
          <div className="profile-identity-card">
            <div className="profile-avatars">
              <div className="profile-discord-avatar">
                <img src={activeUser?.avatarUrl || '/assets/ugeappage-logo-zoomed.webp'} alt={activeUser?.displayName || 'Citizen'} />
                <span className="discord-badge" title="Discord Verified">
                  <DiscordIcon size={12} />
                </span>
              </div>
              {profile?.mcProfile.avatarUrl && (
                <div className="profile-mc-avatar" title={`Minecraft: ${profile.mcProfile.mcUsername}`}>
                  <img src={profile.mcProfile.avatarUrl} alt={profile.mcProfile.mcUsername || 'MC Helm'} />
                  <span className={`mc-live-dot ${profile.mcProfile.online ? 'online' : 'offline'}`} />
                </div>
              )}
            </div>

            <div className="profile-identity-copy">
              <div className="eyebrow">
                <span /> SOVEREIGN CITIZEN DOSSIER • SEASON III
              </div>
              <h1>{activeUser?.displayName}</h1>
              <div className="profile-meta-pills">
                <span className={`citizen-status-pill ${status || 'unapplied'}`}>{statusLabel}</span>
                <span className="meta-chip">
                  <b>Discord:</b> @{activeUser?.username}
                </span>
                <span className="meta-chip">
                  <b>Minecraft IGN:</b> {profile?.mcProfile.mcUsername || activeUser?.mcUsername || 'Not Linked'}
                </span>
                {profile?.mcProfile.linked && (
                  <span className={`meta-chip realm-state ${profile.mcProfile.online ? 'is-online' : ''}`}>
                    <i>●</i> {profile.mcProfile.online ? 'ONLINE IN REALM' : 'OFFLINE'}
                  </span>
                )}
                {profile && profile.mcProfile.playtimeHours > 0 && (
                  <span className="meta-chip">
                    <b>Playtime:</b> {profile.mcProfile.playtimeHours} hrs
                  </span>
                )}
              </div>
            </div>

            <div className="profile-header-actions">
              <button type="button" className="ghost-btn compact-btn" onClick={loadProfile} disabled={loading}>
                <span>{loading ? 'Syncing Ledger...' : '↻ Sync Ledger'}</span>
              </button>
              <button
                type="button"
                className="logout-action-btn"
                onClick={handleLogout}
                disabled={loggingOut}
                title="Sign out of your Discord session"
              >
                <span>{loggingOut ? 'Signing Out...' : 'Sign Out / Logout'}</span>
              </button>
            </div>
          </div>

          {/* Citizenship Status Banner for Pending / Unapplied users */}
          {status !== 'passed' && (
            <div className={`profile-status-banner ${status || 'unapplied'}`}>
              <div>
                <small>CITIZENSHIP & WHITELIST REGISTRY</small>
                <h3>
                  {status === 'pending'
                    ? 'Your Season 3 Whitelist Application is currently being reviewed by Staff.'
                    : status === 'rejected'
                    ? 'Your previous application was not approved. Please contact Staff on Discord.'
                    : 'You have not completed your Season 3 Citizenship Whitelist Application yet.'}
                </h3>
                <p>
                  {status === 'pending'
                    ? `Submitted ${profile?.application?.submittedAt ? new Date(profile.application.submittedAt).toLocaleString() : 'recently'} for Minecraft IGN "${activeUser?.mcUsername}".`
                    : 'Apply through the homepage Citizenship Portal after reading the bilingual Server Constitution.'}
                </p>
              </div>
              {status !== 'pending' && (
                <Link className="primary-btn" to="/">
                  <span>Go to Whitelist Portal</span>
                  <ArrowIcon />
                </Link>
              )}
            </div>
          )}
        </div>
      </section>

      {error && (
        <section className="section-pad">
          <div className="profile-error-box">
            <p>{error}</p>
            <button type="button" className="primary-btn" onClick={loadProfile}>
              <span>Retry</span>
            </button>
          </div>
        </section>
      )}

      {loading && !profile ? (
        <section className="section-pad">
          <div className="profile-loading-card">
            <div className="spinner-ring" />
            <h3>Decrypting Central Bank & Patent Registry Dossier...</h3>
            <p>Synchronizing live balances, physical wallets, tax receipts, and ledger entries.</p>
          </div>
        </section>
      ) : (
        profile && (
          <>
            {/* SECTION 01: SOVEREIGN WEALTH & PHYSICAL WALLETS */}
            <section className="section-pad profile-section">
              <SectionLabel index="01">SOVEREIGN WEALTH & WALLETS</SectionLabel>
              <div className="split-heading" data-reveal>
                <h2>
                  Your treasury.<br />
                  <em>Every rupee verified.</em>
                </h2>
                <p>
                  Live reconciliation of your Imperial State Bank account, physical inventory wallets, and sovereign
                  credit obligations.
                </p>
              </div>

              <div className="profile-kpi-grid" data-reveal>
                <div className="profile-kpi-card highlight">
                  <div className="kpi-top">
                    <small>TOTAL NET WORTH</small>
                    {profile.finances.wealthRank && (
                      <span className="rank-badge">
                        RANK #{profile.finances.wealthRank} / {profile.finances.totalCitizensRanked}
                      </span>
                    )}
                  </div>
                  <b>රු. {formatLkr(profile.finances.netWorth)}</b>
                  <span>Combined Bank Vault + Physical Wallet Cash</span>
                </div>

                <div className="profile-kpi-card">
                  <div className="kpi-top">
                    <small>STATE BANK BALANCE</small>
                    <span className="kpi-tag safe">PROTECTED VAULT</span>
                  </div>
                  <b>රු. {formatLkr(profile.finances.bankBalance)}</b>
                  <span>Imperial State Bank • Interest Eligible</span>
                </div>

                <div className="profile-kpi-card">
                  <div className="kpi-top">
                    <small>PHYSICAL WALLET CASH</small>
                    <span className="kpi-tag gold">{profile.finances.walletCount} WALLET(S)</span>
                  </div>
                  <b>රු. {formatLkr(profile.finances.walletBalance)}</b>
                  <span>Carried in Inventory / Ender Chest</span>
                </div>

                <div className={`profile-kpi-card ${profile.finances.loanDebt > 0 ? 'warning' : ''}`}>
                  <div className="kpi-top">
                    <small>STATE BANK LOAN DEBT</small>
                    <span className={`kpi-tag ${profile.finances.loanDebt > 0 ? 'danger' : 'safe'}`}>
                      {profile.finances.loanDebt > 0 ? 'ACTIVE CREDIT' : 'DEBT FREE'}
                    </span>
                  </div>
                  <b>රු. {formatLkr(profile.finances.loanDebt)}</b>
                  <span>
                    {profile.finances.loanDebt > 0
                      ? 'Outstanding State Credit Facility'
                      : 'No outstanding loan obligations'}
                  </span>
                </div>
              </div>

              {/* Physical Wallets Inventory */}
              <div className="profile-subhead" data-reveal>
                <div>
                  <small>PHYSICAL CURRENCY CARRIERS</small>
                  <h3>Your Inventory Wallets ({profile.finances.walletCount})</h3>
                </div>
                <span className="subhead-note">
                  Last Seen: {formatTimestamp(profile.mcProfile.lastLogoutAt || profile.mcProfile.lastLoginAt)}
                </span>
              </div>

              {profile.finances.wallets.length > 0 ? (
                <div className="wallets profile-wallets-grid" data-reveal>
                  {profile.finances.wallets.map((w) => {
                    const art = walletArtForTier(w.tier);
                    const pct =
                      w.capacity > 0 ? Math.min(100, Math.round((w.balance / w.capacity) * 100)) : w.balance > 0 ? 100 : 12;
                    return (
                      <TiltCard key={w.id} className={w.tier >= 2 ? 'featured' : ''}>
                        <div className={`wallet-art ${art.className}`}>
                          <img src={art.img} alt={w.tierName} />
                        </div>
                        <span>{art.badge}</span>
                        <h3>රු. {formatLkr(w.balance)}</h3>
                        <small>
                          CAPACITY: {w.capacity === -1 ? 'UNLIMITED SOVEREIGN VAULT' : `රු. ${formatLkr(w.capacity)}`}
                        </small>
                        <div className="wallet-cap-bar">
                          <i style={{ width: `${pct}%` }} />
                        </div>
                      </TiltCard>
                    );
                  })}
                </div>
              ) : (
                <div className="profile-empty-panel" data-reveal>
                  <ShieldIcon />
                  <div>
                    <h4>No Physical Wallets Detected in Inventory</h4>
                    <p>
                      You currently have <b>රු. 0</b> in physical wallet cash. Acquire a Leather Wallet (10K cap), Gold
                      Purse (100K cap), or Diamond Briefcase (Unlimited) in-game to carry physical LKR notes.
                    </p>
                  </div>
                </div>
              )}
            </section>

            {/* SECTION 02: STATE TAX BUREAU & CIVIL WELFARE */}
            <section className="section-pad profile-section">
              <SectionLabel index="02">STATE TAX BUREAU & COMPLIANCE</SectionLabel>
              <div className="split-heading" data-reveal>
                <h2>
                  Civic contributions.<br />
                  <em>Audited to the cent.</em>
                </h2>
                <p>
                  Complete record of your State Tax payments, current weekly assessment, and 2% Civil Welfare casualty
                  contributions.
                </p>
              </div>

              <div className="profile-kpi-grid" data-reveal>
                <div className="profile-kpi-card">
                  <div className="kpi-top">
                    <small>TOTAL TAXES PAID</small>
                    <span className="kpi-tag safe">{profile.taxes.receipts.length} RECEIPTS</span>
                  </div>
                  <b>රු. {formatLkr(profile.taxes.totalPaid)}</b>
                  <span>Lifetime Audited State Tax Contributions</span>
                </div>

                <div className={`profile-kpi-card ${profile.taxes.pendingTax > 0 ? 'warning' : ''}`}>
                  <div className="kpi-top">
                    <small>PENDING TAX DUE</small>
                    <span className={`kpi-tag ${profile.taxes.pendingTax > 0 ? 'danger' : 'safe'}`}>
                      {profile.taxes.complianceStatus === 'COMPLIANT' ? '✓ CLEARED' : '⚠ DUE NOW'}
                    </span>
                  </div>
                  <b>රු. {formatLkr(profile.taxes.pendingTax)}</b>
                  <span>
                    {profile.taxes.pendingTax > 0
                      ? 'Settle via State Tax Bureau to avoid Penal Quarry'
                      : 'Zero unpaid tax obligations'}
                  </span>
                </div>

                <div className="profile-kpi-card">
                  <div className="kpi-top">
                    <small>CURRENT WEEKLY INFLOW</small>
                    <span className="kpi-tag">ROLLING CYCLE</span>
                  </div>
                  <b>රු. {formatLkr(profile.taxes.weeklyInflow)}</b>
                  <span>New earnings tracked in current tax window</span>
                </div>

                <div className="profile-kpi-card">
                  <div className="kpi-top">
                    <small>CIVIL WELFARE LEVY (2%)</small>
                    <span className="kpi-tag gold">{profile.taxes.welfareRecords.length} CASUALTIES</span>
                  </div>
                  <b>රු. {formatLkr(profile.taxes.totalWelfareContributed)}</b>
                  <span>Contributed to National Disaster & Welfare Pool</span>
                </div>
              </div>

              {profile.taxes.receipts.length > 0 ? (
                <div className="profile-table-wrap" data-reveal>
                  <div className="profile-table-head">
                    <span>OFFICIAL STATE TAX RECEIPTS</span>
                    <small>{profile.taxes.receipts.length} AUDITED SETTLEMENTS</small>
                  </div>
                  <div className="profile-table">
                    <div className="profile-tr profile-th">
                      <span>RECEIPT ID</span>
                      <span>ASSESSMENT CYCLE</span>
                      <span>DATE & TIME</span>
                      <span>STATUS</span>
                      <span className="right">AMOUNT PAID</span>
                    </div>
                    {profile.taxes.receipts.map((r) => (
                      <div className="profile-tr" key={r.id}>
                        <span className="mono-cell">{r.id}</span>
                        <span>{r.cycle}</span>
                        <span className="muted-cell">{r.date}</span>
                        <span>
                          <em className="status-chip-ok">{r.status}</em>
                        </span>
                        <span className="right amount-out">රු. {formatLkr(r.amount)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="profile-empty-panel" data-reveal>
                  <CheckIcon />
                  <div>
                    <h4>Tax Ledger Clean — No Prior Tax Assessments</h4>
                    <p>
                      Weekly inflows below રુ. 100,000 are in the 0% tax-exempt bracket. Any future tax settlements will
                      appear here with official receipt numbers.
                    </p>
                  </div>
                </div>
              )}
            </section>

            {/* SECTION 03: NATIONAL PATENT AUTHORITY & REGISTERED MACHINERY */}
            <section className="section-pad profile-section">
              <SectionLabel index="03">INDUSTRIAL PATENTS & MONOPOLIES</SectionLabel>
              <div className="split-heading" data-reveal>
                <h2>
                  State charters.<br />
                  <em>Licensed industry.</em>
                </h2>
                <p>
                  Your active 14-day Patent Monopolies registered with the National Patent Authority and serialized
                  industrial machinery.
                </p>
              </div>

              <div className="profile-patent-summary" data-reveal>
                <div>
                  <small>ACTIVE PATENT CHARTERS</small>
                  <b>{profile.patents.ownedCount} / 8 Tiers</b>
                </div>
                <div>
                  <small>14-DAY TOTAL RENEWAL FEE</small>
                  <b>රු. {formatLkr(profile.patents.totalRenewalCost)}</b>
                </div>
                <div>
                  <small>SERIALIZED MACHINES ON GRID</small>
                  <b>{profile.patents.registeredMachinesCount} Units</b>
                </div>
              </div>

              {profile.patents.activePatents.length > 0 ? (
                <div className="profile-patents-grid" data-reveal>
                  {profile.patents.activePatents.map((pat) => {
                    const pct = Math.min(100, Math.max(8, Math.round((pat.remainingDays / 14) * 100)));
                    return (
                      <div className="patent-charter-card" key={pat.tier}>
                        <div className="charter-top">
                          <span className="charter-tier">TIER 0{pat.tier} CHARTER</span>
                          <em className={pat.active ? 'active' : 'expired'}>
                            {pat.active ? `● ACTIVE (${pat.remainingDays}d LEFT)` : '✕ EXPIRED'}
                          </em>
                        </div>
                        <h3>{pat.title}</h3>
                        <p>{pat.sector}</p>
                        <div className="charter-meta">
                          <div>
                            <small>CHARTER VALUE</small>
                            <b>රු. {formatLkr(pat.basePrice)}</b>
                          </div>
                          <div>
                            <small>14-DAY RENEWAL</small>
                            <b>රු. {formatLkr(pat.renewalPrice)}</b>
                          </div>
                          <div>
                            <small>COLLATERAL</small>
                            <b>{pat.collateral}</b>
                          </div>
                          <div>
                            <small>EXPIRES ON</small>
                            <b>{formatTimestamp(pat.expiresAt)}</b>
                          </div>
                        </div>
                        <div className="wallet-cap-bar">
                          <i style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="profile-empty-panel" data-reveal>
                  <SparkIcon />
                  <div>
                    <h4>No Active Industrial Patents Registered</h4>
                    <p>
                      You do not currently hold any Tier 1–8 Patent Monopolies. Visit the Patent Registry in Old Town or
                      New Town to acquire an available industrial charter or commission a licensed Patent Holder via the
                      State Notary.
                    </p>
                  </div>
                </div>
              )}

              {profile.patents.registeredMachines.length > 0 && (
                <div className="profile-table-wrap" data-reveal style={{ marginTop: '24px' }}>
                  <div className="profile-table-head">
                    <span>SERIALIZED INDUSTRIAL MACHINES ({profile.patents.registeredMachinesCount})</span>
                    <small>STATE NOTARY HARDWARE REGISTRY</small>
                  </div>
                  <div className="profile-table">
                    <div className="profile-tr profile-th">
                      <span>SERIAL NUMBER</span>
                      <span>WORLD SECTOR</span>
                      <span>COORDINATES (X, Y, Z)</span>
                      <span className="right">LICENSE EXPIRY</span>
                    </div>
                    {profile.patents.registeredMachines.map((m) => (
                      <div className="profile-tr" key={m.id}>
                        <span className="mono-cell gold-text">{m.serial}</span>
                        <span>{m.world.toUpperCase()}</span>
                        <span className="mono-cell">
                          X: {m.x}, Y: {m.y}, Z: {m.z}
                        </span>
                        <span className="right muted-cell">
                          {m.expiresAt ? formatTimestamp(m.expiresAt) : 'PERMANENT / ACTIVE'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </section>

            {/* SECTION 04: COMPLETE SOVEREIGN TRANSACTION LEDGER */}
            <section className="section-pad profile-section">
              <SectionLabel index="04">COMPLETE TRANSACTION LEDGER</SectionLabel>
              <div className="split-heading" data-reveal>
                <h2>
                  Every transaction.<br />
                  <em>Immutable & traceable.</em>
                </h2>
                <p>
                  Full chronological audit trail of your commodity sales, shop purchases, wire transfers, tax
                  settlements, and patent operations.
                </p>
              </div>

              <div className="profile-patent-summary" data-reveal>
                <div>
                  <small>RECORDED TRANSACTIONS</small>
                  <b>{profile.transactions.totalCount} Entries</b>
                </div>
                <div>
                  <small>TOTAL CREDITS (INFLOW)</small>
                  <b className="emerald-text">+රු. {formatLkr(profile.transactions.totalInflow)}</b>
                </div>
                <div>
                  <small>TOTAL DEBITS (OUTFLOW)</small>
                  <b className="gold-text">-රු. {formatLkr(profile.transactions.totalOutflow)}</b>
                </div>
              </div>

              <div className="tx-controls-bar" data-reveal>
                <div className="tx-filter-pills" role="tablist" aria-label="Transaction category filter">
                  {(
                    [
                      ['all', 'All Entries'],
                      ['income', 'Inflow (+)'],
                      ['outflow', 'Outflow (−)'],
                      ['market', 'State Depot'],
                      ['shop', 'Shop Purchases'],
                      ['transfer', 'Transfers'],
                      ['tax', 'Taxes & Welfare'],
                      ['patent', 'Patents & Loans'],
                    ] as Array<[TxFilter, string]>
                  ).map(([key, label]) => (
                    <button
                      key={key}
                      type="button"
                      className={txFilter === key ? 'active' : ''}
                      onClick={() => setTxFilter(key)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <input
                  type="search"
                  className="tx-search-input"
                  placeholder="Search item, counterparty, date, or amount..."
                  value={txSearch}
                  onChange={(e) => setTxSearch(e.target.value)}
                />
              </div>

              {filteredTransactions.length > 0 ? (
                <div className="profile-table-wrap" data-reveal>
                  <div className="profile-table-head">
                    <span>CENTRAL BANK AUDIT LOG</span>
                    <small>SHOWING {filteredTransactions.length} TRANSACTIONS</small>
                  </div>
                  <div className="profile-table tx-ledger-table">
                    <div className="profile-tr profile-th">
                      <span>DATE & TIME</span>
                      <span>CATEGORY</span>
                      <span>OPERATION</span>
                      <span>COUNTERPARTY / ITEM</span>
                      <span className="right">AMOUNT (LKR)</span>
                    </div>
                    {filteredTransactions.map((tx) => (
                      <div className="profile-tr" key={tx.id}>
                        <span className="mono-cell muted-cell">{tx.date}</span>
                        <span>
                          <em className={`tx-cat-badge ${tx.category}`}>{tx.categoryLabel}</em>
                        </span>
                        <span>{tx.type}</span>
                        <span className="mono-cell">{tx.target}</span>
                        <span className={`right mono-cell ${tx.isIncome ? 'amount-in' : 'amount-out'}`}>
                          {tx.amount}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="profile-empty-panel" data-reveal>
                  <ShieldIcon />
                  <div>
                    <h4>No Matching Transactions Found</h4>
                    <p>
                      {profile.transactions.totalCount === 0
                        ? 'No in-game financial transactions have been recorded for your account yet.'
                        : 'No transactions matched your current filter or search query.'}
                    </p>
                  </div>
                </div>
              )}
            </section>
          </>
        )
      )}
    </>
  );
}
